import { ReactComponent as FathomLogo } from '@/assets/fathom.svg'
import { ReactComponent as PersonIcon } from '@/assets/icons/person.svg'
import { ReactComponent as PersonIndividualIcon } from '@/assets/icons/person-individual.svg'
import { ReactComponent as PersonTeamIcon } from '@/assets/icons/person-team.svg'
import { ReactComponent as CheckIcon } from '@/assets/icons/check.svg'
import { CsrfInput } from '@/web-app/views/users/components/csrf-input'
import { usePageProps } from '@/shared/hooks/use-page-props'
import { useCurrentWebUser } from '@/web-app/providers/current-user-provider'
import { Circle } from 'lucide-react'

interface IConfirmPersonalEmailPageProps {
  continueWithWorkEmailUrl: string
  continueWithPersonalEmailUrl: string
}

function PersonalEmailCard({ continueWithPersonalEmailUrl }: { continueWithPersonalEmailUrl: string }) {
  return (
    <div className='fv:order-2 fv:lg:order-1 fv:w-full fv:max-w-lg fv:lg:max-w-none fv:backdrop-blur-sm fv:bg-white/3 fv:border fv:border-white/10 fv:rounded-[2rem] fv:p-10 fv:flex fv:flex-col fv:gap-8 fv:flex-1'>
      <div className='fv:flex fv:flex-col fv:items-center fv:gap-4'>
        <div className='fv:size-9'>
          <PersonIcon className='fv:size-full' />
        </div>
        <div className='fv:text-center'>
          <p className='fv:text-2xl fv:font-semibold fv:text-off-white fv:tracking-tight'>Personal use only</p>
          <p className='fv:text-sm fv:text-gray-400 fv:mt-1'>Suited for one-off calls</p>
        </div>
      </div>

      <ul className='fv:flex fv:flex-col fv:gap-2.5 fv:flex-1'>
        {['View only your own meetings', 'No shared workspace', 'Cannot convert to a team plan later'].map((item) => (
          <li key={item} className='fv:flex fv:items-center fv:gap-2'>
            <div className='fv:size-4 fv:shrink-0 fv:flex fv:items-center fv:justify-center'>
              <Circle className='fv:size-3 fv:text-gray-500' />
            </div>
            <span className='fv:text-sm fv:text-gray-300 fv:tracking-tight'>{item}</span>
          </li>
        ))}
      </ul>

      <form method='post' action={continueWithPersonalEmailUrl}>
        <CsrfInput />
        <button
          type='submit'
          className='fv:w-full fv:bg-white/12 fv:rounded-lg fv:py-3 fv:text-base fv:text-gray-300 fv:hover:opacity-80 fv:transition fv:duration-200'
        >
          Continue with Personal Email
        </button>
      </form>
    </div>
  )
}

interface WorkEmailCardProps {
  continueWithWorkEmailUrl: string
}

function WorkEmailCard({ continueWithWorkEmailUrl }: WorkEmailCardProps) {
  return (
    <div className='fv:order-1 fv:lg:order-2 fv:w-full fv:max-w-lg fv:lg:max-w-none fv:relative fv:backdrop-blur-sm fv:bg-white/5 fv:border fv:border-white/25 fv:rounded-[2rem] fv:p-10 fv:flex fv:flex-col fv:gap-8 fv:flex-1'>
      <div className='fv:absolute fv:top-4 fv:right-4 fv:bg-white/6 fv:rounded-full fv:px-2.5 fv:py-1'>
        <span className='fv:text-xs fv:font-medium fv:text-gray-400 fv:uppercase fv:tracking-wide'>Recommended</span>
      </div>

      <div className='fv:flex fv:flex-col fv:items-center fv:gap-4 fv:pt-5'>
        <div className='fv:flex fv:items-center fv:gap-2.5'>
          <div className='fv:size-9'>
            <PersonIndividualIcon className='fv:size-full' />
          </div>
          <div className='fv:size-9'>
            <PersonTeamIcon className='fv:size-full' />
          </div>
        </div>
        <div className='fv:text-center'>
          <p className='fv:text-2xl fv:font-semibold fv:text-off-white fv:tracking-tight'>Individual or Team use</p>
          <p className='fv:text-sm fv:text-gray-400 fv:mt-1'>Most powerful features &amp; insights</p>
        </div>
      </div>

      <ul className='fv:flex fv:flex-col fv:gap-2.5 fv:flex-1'>
        {['Set individual and team preferences', 'Create private and shared workspaces', 'Add teammates anytime'].map(
          (item) => (
            <li key={item} className='fv:flex fv:items-center fv:gap-2'>
              <div className='fv:size-4 fv:shrink-0'>
                <CheckIcon className='fv:size-full' />
              </div>
              <span className='fv:text-sm fv:text-white fv:tracking-tight'>{item}</span>
            </li>
          ),
        )}
      </ul>

      <form method='post' action={continueWithWorkEmailUrl}>
        <CsrfInput />
        <button
          type='submit'
          className='fv:w-full fv:bg-accent-fathom fv:rounded-lg fv:py-3 fv:text-base fv:font-semibold fv:text-black fv:hover:opacity-90 fv:transition fv:duration-200'
        >
          Continue with Work Email
        </button>
      </form>
    </div>
  )
}

export default function ConfirmPersonalEmail() {
  const { pageProps } = usePageProps<IConfirmPersonalEmailPageProps>()
  const { currentUser } = useCurrentWebUser()

  const { continueWithWorkEmailUrl, continueWithPersonalEmailUrl } = pageProps ?? {}
  const email = currentUser?.email

  return (
    <div className='fv:flex fv:flex-col fv:min-h-screen fv:bg-black fv:overflow-auto fv:px-5 fv:py-10 fv:lg:py-11'>
      <div className='fv:flex fv:justify-center fv:mb-10 fv:lg:mb-24'>
        <FathomLogo className='fv:w-44 fv:text-off-white' />
      </div>

      <div className='fv:flex fv:flex-col fv:items-center fv:gap-8 fv:lg:gap-12 fv:w-full fv:max-w-4xl fv:mx-auto'>
        <div className='fv:flex fv:flex-col fv:items-center fv:gap-4 fv:text-center fv:max-w-2xl fv:px-1'>
          <h1 className='fv:text-2xl fv:lg:text-3xl fv:font-semibold fv:leading-tight fv:tracking-tight fv:lg:tracking-tighter'>
            <span className='fv:text-off-white fv:lg:bg-gradient-to-r fv:lg:from-orange-500 fv:lg:to-yellow-200 fv:lg:bg-clip-text fv:lg:text-transparent'>
              Are your meetings on your company calendar?
            </span>
            <span className='fv:block fv:text-gray-300'>We recommend using your work email.</span>
          </h1>
          {email && (
            <p className='fv:text-sm fv:text-gray-400'>
              <span className='fv:font-semibold fv:text-white'>{email}</span> looks like a personal email
            </p>
          )}
        </div>

        <div className='fv:flex fv:flex-col fv:items-center fv:lg:flex-row fv:lg:items-stretch fv:gap-10 fv:w-full'>
          <PersonalEmailCard continueWithPersonalEmailUrl={continueWithPersonalEmailUrl ?? ''} />
          <WorkEmailCard continueWithWorkEmailUrl={continueWithWorkEmailUrl ?? ''} />
        </div>
      </div>
    </div>
  )
}
